import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateInput, convertToISO, displayDateValue, formatSSN, formatPhoneNumber } from '../utils/dateUtils';
import { validateEmail } from '../utils/emailValidator';
import { validatePage1, validateField, validateDateOfBirth, validateExpirationDate } from '../utils/formValidation';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useOrganization } from '../contexts/OrganizationContext';

function Page1({ formData, updateFormData, onNext }) {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const formRef = useRef(null);

  // Comprehensive validation state
  const [errors, setErrors] = useState({});
  const [dateErrors, setDateErrors] = useState({}); // Keep for backward compatibility with existing date error display
  const [emailValidation, setEmailValidation] = useState({ isValid: true, error: null, suggestion: null, correctedEmail: null });
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate field on blur
  const handleFieldBlur = (e) => {
    const { name, value } = e.target;

    // Validate the specific field
    const result = validateField(name, value, formData);
    if (!result.isValid) {
      setErrors(prev => ({ ...prev, [name]: result.error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatDateInput(value);

    // Clear validation errors when user is typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Store the formatted value for display
    // Only convert to ISO when complete (10 characters)
    if (formatted.length === 10) {
      const iso = convertToISO(formatted);

      // Check if this is an expiration date field and validate it's not in the past
      const isExpirationField = name === 'idExpirationDate' || name === 'spouseIdExpirationDate';
      const isDobField = name === 'dateOfBirth' || name === 'spouseDateOfBirth';

      if (isExpirationField) {
        // Validate expiration date format and future date
        const expResult = validateExpirationDate(formatted);
        if (!expResult.isValid) {
          setDateErrors(prev => ({ ...prev, [name]: true }));
          setErrors(prev => ({ ...prev, [name]: expResult.error }));
          updateFormData({ [name]: formatted });
          return;
        } else {
          setDateErrors(prev => ({ ...prev, [name]: false }));
        }
      } else if (isDobField) {
        // Validate date of birth format
        const dobResult = validateDateOfBirth(formatted);
        if (!dobResult.isValid) {
          setErrors(prev => ({ ...prev, [name]: dobResult.error }));
          updateFormData({ [name]: formatted });
          return;
        }
      }

      updateFormData({ [name]: iso });
    } else {
      // Clear error when user is typing (incomplete date)
      if (dateErrors[name]) {
        setDateErrors(prev => ({ ...prev, [name]: false }));
      }
      // Store the partial formatted value temporarily
      updateFormData({ [name]: formatted });
    }
  };

  const handleSSNChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatSSN(value);
    updateFormData({ [name]: formatted });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhoneNumber(value);
    updateFormData({ [name]: formatted });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Email change handler - NO real-time validation (cleaner UX while typing)
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear validation state - don't validate while typing
    // Full validation happens on "Next" click, gentle suggestions on blur
    setEmailValidation({ isValid: true, error: null, suggestion: null, correctedEmail: null });
  };

  // Email blur handler - show gentle suggestions only (not errors)
  const handleEmailBlur = (e) => {
    const value = e.target.value;

    // Only show gentle suggestion if email looks almost complete
    if (value && value.includes('@') && value.length > 5) {
      const validation = validateEmail(value);

      // Only show if there's a correctable suggestion (like "@gmail.com" instead of "@gmial.com")
      // Don't show basic errors like "must include @" on blur
      if (validation.correctedEmail) {
        setEmailValidation(validation);
      }
    }
  };

  // Apply email correction when user clicks suggestion
  const applyEmailSuggestion = () => {
    if (emailValidation.correctedEmail) {
      updateFormData({ email: emailValidation.correctedEmail });
      setEmailValidation({ isValid: true, error: null, suggestion: null, correctedEmail: null });
    }
  };

  // Memoized to prevent unnecessary re-renders (though AddressAutocomplete now uses refs)
  const handleAddressSelect = useCallback((addressData) => {
    console.log('handleAddressSelect called with:', addressData);

    // Use a single batch update with a slight delay to ensure React processes it
    setTimeout(() => {
      updateFormData({
        streetAddress: addressData.streetAddress,
        streetAddress2: addressData.subpremise || '', // Capture apartment/unit from Google Maps
        city: addressData.city,
        state: addressData.state,
        postalCode: addressData.postalCode
      });
      console.log('Form data updated with address:', {
        street: addressData.streetAddress,
        apt: addressData.subpremise,
        city: addressData.city,
        state: addressData.state,
        zip: addressData.postalCode
      });
    }, 0);
  }, [updateFormData]);

  const handleDependentChange = (index, field, value) => {
    const newDependents = [...(formData.dependents || [])];
    if (!newDependents[index]) {
      newDependents[index] = {};
    }
    newDependents[index][field] = value;
    updateFormData({ dependents: newDependents });
  };

  const handleDependentDateChange = (index, field, value) => {
    const formatted = formatDateInput(value);
    const errorKey = `dependent${index}DateOfBirth`;

    // Clear error when user is typing
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }

    // Only convert to ISO when complete (10 characters)
    if (formatted.length === 10) {
      const iso = convertToISO(formatted);

      // Validate dependent DOB - must be in the past
      if (field === 'dateOfBirth') {
        const dobResult = validateDateOfBirth(formatted);
        if (!dobResult.isValid) {
          setErrors(prev => ({ ...prev, [errorKey]: dobResult.error }));
          handleDependentChange(index, field, formatted); // Store formatted, not ISO
          return;
        }
      }

      handleDependentChange(index, field, iso);
    } else {
      // Store the partial formatted value temporarily
      handleDependentChange(index, field, formatted);
    }
  };

  const handleDependentSSNChange = (index, field, value) => {
    const formatted = formatSSN(value);
    handleDependentChange(index, field, formatted);
  };

  const handleNext = (e) => {
    e.preventDefault();

    // CRITICAL: Sync DOM values to state before validation
    // This catches browser autofill values that don't trigger onChange events
    const formElement = formRef.current;
    let dataToValidate = { ...formData };

    if (formElement) {
      const inputs = formElement.querySelectorAll('input, select, textarea');
      const autofillUpdates = {};

      inputs.forEach(input => {
        if (input.name && input.value) {
          const currentValue = formData[input.name];
          const domValue = input.value.trim();

          // If DOM has a value but React state is empty/different, use DOM value
          if (domValue && (!currentValue || currentValue.trim() === '')) {
            autofillUpdates[input.name] = domValue;
            console.log(`📋 Autofill detected for ${input.name}:`, domValue);
          }
        }
      });

      // If we found autofilled values, update state
      if (Object.keys(autofillUpdates).length > 0) {
        console.log('🔄 Syncing autofill values to state:', autofillUpdates);
        updateFormData(autofillUpdates);
        // Merge with validation data
        dataToValidate = { ...dataToValidate, ...autofillUpdates };
      }
    }

    // Run comprehensive validation on all Page 1 fields
    const validationResult = validatePage1(dataToValidate);

    if (!validationResult.isValid) {
      // Set all errors at once
      setErrors(validationResult.errors);

      // Update email validation state if email has error
      if (validationResult.errors.email) {
        const emailCheck = validateEmail(dataToValidate.email);
        setEmailValidation(emailCheck);
      }

      // Scroll to first error field
      const firstErrorKey = Object.keys(validationResult.errors)[0];
      scrollToField(firstErrorKey);

      return;
    }

    // SMS consent check for Gator Agent
    if (organization?.name?.includes('Gator Agent') && !smsConsent) {
      setSmsConsentError(true);
      const consentEl = document.getElementById('sms-consent-checkbox');
      if (consentEl) consentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // All validations passed - proceed to next page
    onNext();
  };

  // Helper to scroll to a field by name
  const scrollToField = (fieldName) => {
    // Map field names to input names or IDs
    let selector = `[name="${fieldName}"]`;

    // Handle dependent fields specially
    if (fieldName.startsWith('dependent')) {
      const match = fieldName.match(/dependent(\d+)/);
      if (match) {
        const index = match[1];
        // Find the dependent section and scroll to it
        selector = `.dependent-section-${index}`;
      }
    }

    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus the element after scrolling
      setTimeout(() => {
        element.focus();
      }, 300);
    } else {
      // Fallback: scroll to top of form
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Determine if spouse fields should show
  const showSpouseFields = formData.filingStatus === 'Married Filing Jointly';

  // Determine number of dependent sections to show
  const numDependents = parseInt(formData.numberOfDependents) || 0;

  return (
    <form ref={formRef} onSubmit={handleNext}>
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {organization?.name ? (
            t('page1.header.title', { organizationName: organization.name })
          ) : (
            <span className="animate-pulse text-gray-400">Loading...</span>
          )}
        </h1>
        <p className="text-lg text-gray-600">{t('page1.header.subtitle')}</p>
      </div>

      {/* Taxpayer Name */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-3">
          {t('page1.taxpayerName')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              name="taxpayerFirstName"
              value={formData.taxpayerFirstName}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.taxpayerFirstName ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.taxpayerFirstName ? (
              <p className="text-xs text-red-600 mt-1">{errors.taxpayerFirstName}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">{t('common.firstName')}</p>
            )}
          </div>
          <div>
            <input
              type="text"
              name="taxpayerMiddleName"
              value={formData.taxpayerMiddleName}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.taxpayerMiddleName ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
            {errors.taxpayerMiddleName ? (
              <p className="text-xs text-red-600 mt-1">{errors.taxpayerMiddleName}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">{t('common.middleName')}</p>
            )}
          </div>
          <div>
            <input
              type="text"
              name="taxpayerLastName"
              value={formData.taxpayerLastName}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.taxpayerLastName ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.taxpayerLastName ? (
              <p className="text-xs text-red-600 mt-1">{errors.taxpayerLastName}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">{t('common.lastName')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Date of Birth */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.dateOfBirth')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          name="dateOfBirth"
          value={displayDateValue(formData.dateOfBirth)}
          onChange={handleDateChange}
          onBlur={handleFieldBlur}
          placeholder="MM/DD/YYYY"
          maxLength="10"
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.dateOfBirth ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        />
        {errors.dateOfBirth ? (
          <p className="text-xs text-red-600 mt-1">{errors.dateOfBirth}</p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">MM/DD/YYYY</p>
        )}
      </div>

      {/* Filing Status */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.filingStatus.label')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <select
          name="filingStatus"
          value={formData.filingStatus}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.filingStatus ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        >
          <option value="">{t('common.pleaseSelect')}</option>
          <option value="Single">{t('page1.filingStatus.single')}</option>
          <option value="Head of Household">{t('page1.filingStatus.headOfHousehold')}</option>
          <option value="Married Filing Single">{t('page1.filingStatus.marriedFilingSingle')}</option>
          <option value="Married Filing Jointly">{t('page1.filingStatus.marriedFilingJointly')}</option>
        </select>
        {errors.filingStatus && (
          <p className="text-xs text-red-600 mt-1">{errors.filingStatus}</p>
        )}
      </div>

      {/* Source of Income */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.incomeSource.label')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <select
          name="incomeSource"
          value={formData.incomeSource}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.incomeSource ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        >
          <option value="">{t('common.pleaseSelect')}</option>
          <option value="W-2">{t('page1.incomeSource.w2')}</option>
          <option value="1099">{t('page1.incomeSource.1099')}</option>
          <option value="Self Employment">{t('page1.incomeSource.selfEmployment')}</option>
          <option value="W-2 & 1099">{t('page1.incomeSource.w2And1099')}</option>
          <option value="W-2 & Self Employment">{t('page1.incomeSource.w2AndSelfEmployment')}</option>
          <option value="W-2, Self Employment and 1099">{t('page1.incomeSource.w2SelfEmploymentAnd1099')}</option>
          <option value="Social Security Income">{t('page1.incomeSource.socialSecurityIncome')}</option>
          <option value="Social Security Income and 1099">{t('page1.incomeSource.socialSecurityIncomeAnd1099')}</option>
          <option value="Social Security Income and Self Employment">{t('page1.incomeSource.socialSecurityIncomeAndSelfEmployment')}</option>
        </select>
        {errors.incomeSource && (
          <p className="text-xs text-red-600 mt-1">{errors.incomeSource}</p>
        )}
      </div>

      {/* Address */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.address.label')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <div className="space-y-3">
          <div>
            <AddressAutocomplete
              value={formData.streetAddress || ''}
              onAddressSelect={handleAddressSelect}
              placeholder={t('page1.address.street')}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${
                errors.streetAddress ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.streetAddress && (
              <p className="text-xs text-red-600 mt-1">{errors.streetAddress}</p>
            )}
          </div>
          <input
            type="text"
            name="streetAddress2"
            value={formData.streetAddress2}
            onChange={handleInputChange}
            placeholder={t('page1.address.street2')}
            className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                placeholder={t('page1.address.city')}
                className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.city ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                required
              />
              {errors.city && (
                <p className="text-xs text-red-600 mt-1">{errors.city}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                placeholder={t('page1.address.state')}
                className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.state ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                required
              />
              {errors.state && (
                <p className="text-xs text-red-600 mt-1">{errors.state}</p>
              )}
            </div>
          </div>
          <div>
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder={t('page1.address.postalCode')}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.postalCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.postalCode && (
              <p className="text-xs text-red-600 mt-1">{errors.postalCode}</p>
            )}
          </div>
        </div>
      </div>

      {/* Phone Number */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.phoneNumber')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="tel"
          inputMode="numeric"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handlePhoneChange}
          onBlur={handleFieldBlur}
          placeholder="(XXX) XXX-XXXX"
          maxLength="14"
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.phoneNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        />
        {errors.phoneNumber && (
          <p className="text-xs text-red-600 mt-1">{errors.phoneNumber}</p>
        )}
      </div>

      {/* SMS Opt-In — Gator Agent only */}
      {organization?.name?.includes('Gator Agent') && (
        <div
          id="sms-consent-checkbox"
          className={`mb-6 p-4 rounded-lg border ${
            smsConsentError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => {
                setSmsConsent(e.target.checked);
                if (e.target.checked) setSmsConsentError(false);
              }}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              I agree to receive SMS text messages from <strong>Gator Agent</strong> for appointment reminders, document status updates, and service follow-ups. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help. View our{' '}
              <a
                href="https://www.gatoragent.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800 font-medium"
              >
                Privacy Policy
              </a>
              {' '}and{' '}
              <a
                href="https://www.gatoragent.com/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800 font-medium"
              >
                Terms & Conditions
              </a>.
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {smsConsentError && (
            <p className="text-xs text-red-600 mt-2 ml-7">
              You must agree to the SMS terms to continue.
            </p>
          )}
        </div>
      )}

      {/* Email with Typo Detection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.email')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          placeholder={t('page1.emailPlaceholder')}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.email || !emailValidation.isValid ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        />
        {/* Email validation error/suggestion */}
        {(errors.email || (!emailValidation.isValid && emailValidation.error)) && (
          <div className="mt-1">
            {emailValidation.correctedEmail ? (
              <button
                type="button"
                onClick={applyEmailSuggestion}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {emailValidation.error} Click to fix.
              </button>
            ) : (
              <p className="text-xs text-red-600">{errors.email || emailValidation.error}</p>
            )}
          </div>
        )}
      </div>

      {/* SSN */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.ssn')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          name="ssn"
          value={formData.ssn}
          onChange={handleSSNChange}
          onBlur={handleFieldBlur}
          placeholder="XXX-XX-XXXX"
          maxLength="11"
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.ssn ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        />
        {errors.ssn && (
          <p className="text-xs text-red-600 mt-1">{errors.ssn}</p>
        )}
      </div>

      {/* Driver's License */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.driversLicense')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="text"
          name="driversLicense"
          value={formData.driversLicense}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.driversLicense ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        />
        {errors.driversLicense && (
          <p className="text-xs text-red-600 mt-1">{errors.driversLicense}</p>
        )}
      </div>

      {/* ID Expiration */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.idExpiration')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          name="idExpirationDate"
          value={displayDateValue(formData.idExpirationDate)}
          onChange={handleDateChange}
          onBlur={handleFieldBlur}
          placeholder="MM/DD/YYYY"
          maxLength="10"
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.idExpirationDate || dateErrors.idExpirationDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        />
        {errors.idExpirationDate || dateErrors.idExpirationDate ? (
          <p className="text-xs text-red-600 mt-1">{errors.idExpirationDate || 'ID expiration date must be in the future'}</p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">MM/DD/YYYY (Must not be expired)</p>
        )}
      </div>

      {/* Health Insurance Questions */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.healthInsurance.has')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <select
          name="hasMarketplaceInsurance"
          value={formData.hasMarketplaceInsurance}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.hasMarketplaceInsurance ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        >
          <option value="">{t('common.pleaseSelect')}</option>
          <option value="Yes">{t('common.yes')}</option>
          <option value="No">{t('common.no')}</option>
        </select>
        {errors.hasMarketplaceInsurance && (
          <p className="text-xs text-red-600 mt-1">{errors.hasMarketplaceInsurance}</p>
        )}
      </div>

      {/* Only show if they said No to marketplace insurance */}
      {formData.hasMarketplaceInsurance === 'No' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('page1.healthInsurance.wants')} <span className="text-red-500">{t('common.required')}</span>
          </label>
          <select
            name="wantsHealthInsurance"
            value={formData.wantsHealthInsurance}
            onChange={handleInputChange}
            onBlur={handleFieldBlur}
            className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.wantsHealthInsurance ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          >
            <option value="">{t('common.pleaseSelect')}</option>
            <option value="Yes">{t('common.yes')}</option>
            <option value="No">{t('common.no')}</option>
            <option value="I already have health insurance through my employer">{t('page1.healthInsurance.hasEmployer')}</option>
            <option value="I have Medicaid">{t('page1.healthInsurance.hasMedicaid')}</option>
          </select>
          {errors.wantsHealthInsurance && (
            <p className="text-xs text-red-600 mt-1">{errors.wantsHealthInsurance}</p>
          )}
        </div>
      )}

      {/* Life Insurance Questions */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.lifeInsurance.has')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <select
          name="hasLifeInsurance"
          value={formData.hasLifeInsurance}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.hasLifeInsurance ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        >
          <option value="">{t('common.pleaseSelect')}</option>
          <option value="Yes">{t('common.yes')}</option>
          <option value="No">{t('common.no')}</option>
        </select>
        {errors.hasLifeInsurance && (
          <p className="text-xs text-red-600 mt-1">{errors.hasLifeInsurance}</p>
        )}
      </div>

      {/* Only show if they said No to life insurance */}
      {formData.hasLifeInsurance === 'No' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('page1.lifeInsurance.wants')} <span className="text-red-500">{t('common.required')}</span>
          </label>
          <select
            name="wantsLifeInsurance"
            value={formData.wantsLifeInsurance}
            onChange={handleInputChange}
            onBlur={handleFieldBlur}
            className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.wantsLifeInsurance ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          >
            <option value="">{t('common.pleaseSelect')}</option>
            <option value="Yes">{t('common.yes')}</option>
            <option value="No">{t('common.no')}</option>
            <option value="I already have life insurance through my employer">{t('page1.lifeInsurance.hasEmployer')}</option>
          </select>
          {errors.wantsLifeInsurance && (
            <p className="text-xs text-red-600 mt-1">{errors.wantsLifeInsurance}</p>
          )}
        </div>
      )}

      {/* Home Ownership */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.home.ownership')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <select
          name="homeOwnership"
          value={formData.homeOwnership}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.homeOwnership ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        >
          <option value="">{t('common.pleaseSelect')}</option>
          <option value="Rent">{t('page1.home.rent')}</option>
          <option value="Own">{t('page1.home.own')}</option>
        </select>
        {errors.homeOwnership && (
          <p className="text-xs text-red-600 mt-1">{errors.homeOwnership}</p>
        )}
      </div>

      {/* Only show if they said Rent */}
      {formData.homeOwnership === 'Rent' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('page1.home.wantToBuy')} <span className="text-red-500">{t('common.required')}</span>
          </label>
          <select
            name="wantsToBuyHome"
            value={formData.wantsToBuyHome}
            onChange={handleInputChange}
            onBlur={handleFieldBlur}
            className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.wantsToBuyHome ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          >
            <option value="">{t('common.pleaseSelect')}</option>
            <option value="Yes">{t('common.yes')}</option>
            <option value="No">{t('common.no')}</option>
            <option value="I need to work on my credit.">{t('page1.home.workOnCredit')}</option>
          </select>
          {errors.wantsToBuyHome && (
            <p className="text-xs text-red-600 mt-1">{errors.wantsToBuyHome}</p>
          )}
        </div>
      )}

      {/* Spouse Information - Only show if Married Filing Jointly */}
      {showSpouseFields && (
        <>
          <div className="border-t pt-6 mt-8 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page1.spouse.title')}</h2>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-3">
              {t('page1.spouse.name')} <span className="text-red-500">{t('common.required')}</span>
            </label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <input
                  type="text"
                  name="spouseFirstName"
                  value={formData.spouseFirstName}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.spouseFirstName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.spouseFirstName ? (
                  <p className="text-xs text-red-600 mt-1">{errors.spouseFirstName}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">{t('common.firstName')}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  name="spouseMiddleName"
                  value={formData.spouseMiddleName}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.spouseMiddleName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.spouseMiddleName ? (
                  <p className="text-xs text-red-600 mt-1">{errors.spouseMiddleName}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">{t('common.middleName')}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  name="spouseLastName"
                  value={formData.spouseLastName}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.spouseLastName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.spouseLastName ? (
                  <p className="text-xs text-red-600 mt-1">{errors.spouseLastName}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">{t('common.lastName')}</p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('page1.spouse.dateOfBirth')} <span className="text-red-500">{t('common.required')}</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="spouseDateOfBirth"
              value={displayDateValue(formData.spouseDateOfBirth)}
              onChange={handleDateChange}
              onBlur={handleFieldBlur}
              placeholder="MM/DD/YYYY"
              maxLength="10"
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.spouseDateOfBirth ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.spouseDateOfBirth ? (
              <p className="text-xs text-red-600 mt-1">{errors.spouseDateOfBirth}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">MM/DD/YYYY</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('page1.spouse.ssn')} <span className="text-red-500">{t('common.required')}</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="spouseSsn"
              value={formData.spouseSsn}
              onChange={handleSSNChange}
              onBlur={handleFieldBlur}
              placeholder="XXX-XX-XXXX"
              maxLength="11"
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.spouseSsn ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.spouseSsn && (
              <p className="text-xs text-red-600 mt-1">{errors.spouseSsn}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('page1.spouse.driversLicense')} <span className="text-red-500">{t('common.required')}</span>
            </label>
            <input
              type="text"
              name="spouseDriversLicense"
              value={formData.spouseDriversLicense}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.spouseDriversLicense ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.spouseDriversLicense && (
              <p className="text-xs text-red-600 mt-1">{errors.spouseDriversLicense}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('page1.spouse.idExpiration')} <span className="text-red-500">{t('common.required')}</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="spouseIdExpirationDate"
              value={displayDateValue(formData.spouseIdExpirationDate)}
              onChange={handleDateChange}
              onBlur={handleFieldBlur}
              placeholder="MM/DD/YYYY"
              maxLength="10"
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.spouseIdExpirationDate || dateErrors.spouseIdExpirationDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            />
            {errors.spouseIdExpirationDate || dateErrors.spouseIdExpirationDate ? (
              <p className="text-xs text-red-600 mt-1">{errors.spouseIdExpirationDate || 'ID expiration date must be in the future'}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">MM/DD/YYYY (Must not be expired)</p>
            )}
          </div>
        </>
      )}

      {/* Dependents Section */}
      <div className="border-t pt-6 mt-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page1.dependents.title')}</h2>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('page1.dependents.claiming')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <select
          name="claimingDependents"
          value={formData.claimingDependents}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.claimingDependents ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          required
        >
          <option value="">{t('common.pleaseSelect')}</option>
          <option value="Yes">{t('common.yes')}</option>
          <option value="No">{t('common.no')}</option>
        </select>
        {errors.claimingDependents && (
          <p className="text-xs text-red-600 mt-1">{errors.claimingDependents}</p>
        )}
      </div>

      {formData.claimingDependents === 'Yes' && (
        <>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('page1.dependents.howMany')} <span className="text-red-500">{t('common.required')}</span>
            </label>
            <select
              name="numberOfDependents"
              value={formData.numberOfDependents}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.numberOfDependents ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              required
            >
              <option value="">{t('common.pleaseSelect')}</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
            </select>
            {errors.numberOfDependents && (
              <p className="text-xs text-red-600 mt-1">{errors.numberOfDependents}</p>
            )}
          </div>

          {/* Dependent Forms */}
          {Array.from({ length: numDependents }, (_, i) => (
            <div key={i} className={`dependent-section-${i} mb-8 p-6 bg-gray-50 rounded-lg border ${
              errors[`dependent${i}FirstName`] || errors[`dependent${i}LastName`] || errors[`dependent${i}Ssn`] || errors[`dependent${i}DateOfBirth`] || errors[`dependent${i}Relationship`] || errors[`dependent${i}Activity`]
                ? 'border-red-300' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('page1.dependents.dependent')} {i + 1}</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  {t('page1.dependents.dependent')} {i + 1} <span className="text-red-500">{t('common.required')}</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <input
                      type="text"
                      value={formData.dependents[i]?.firstName || ''}
                      onChange={(e) => handleDependentChange(i, 'firstName', e.target.value)}
                      className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors[`dependent${i}FirstName`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors[`dependent${i}FirstName`] ? (
                      <p className="text-xs text-red-600 mt-1">{errors[`dependent${i}FirstName`]}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">{t('common.firstName')}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.dependents[i]?.middleName || ''}
                      onChange={(e) => handleDependentChange(i, 'middleName', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('common.middleName')}</p>
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.dependents[i]?.lastName || ''}
                      onChange={(e) => handleDependentChange(i, 'lastName', e.target.value)}
                      className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors[`dependent${i}LastName`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors[`dependent${i}LastName`] ? (
                      <p className="text-xs text-red-600 mt-1">{errors[`dependent${i}LastName`]}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">{t('common.lastName')}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('page1.dependents.relationship')} <span className="text-red-500">{t('common.required')}</span>
                </label>
                <select
                  value={formData.dependents[i]?.relationship || ''}
                  onChange={(e) => handleDependentChange(i, 'relationship', e.target.value)}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors[`dependent${i}Relationship`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="Son">{t('page1.dependents.relationships.son')}</option>
                  <option value="Daughter">{t('page1.dependents.relationships.daughter')}</option>
                  <option value="Stepchild">{t('page1.dependents.relationships.stepchild')}</option>
                  <option value="Eligible Foster Child">{t('page1.dependents.relationships.fosterChild')}</option>
                  <option value="Brother">{t('page1.dependents.relationships.brother')}</option>
                  <option value="Sister">{t('page1.dependents.relationships.sister')}</option>
                  <option value="Step Brother">{t('page1.dependents.relationships.stepBrother')}</option>
                  <option value="Step Sister">{t('page1.dependents.relationships.stepSister')}</option>
                  <option value="Adopted Child">{t('page1.dependents.relationships.adoptedChild')}</option>
                  <option value="Niece">{t('page1.dependents.relationships.niece')}</option>
                  <option value="Nephew">{t('page1.dependents.relationships.nephew')}</option>
                  <option value="Father">{t('page1.dependents.relationships.father')}</option>
                  <option value="Mother">{t('page1.dependents.relationships.mother')}</option>
                  <option value="Grandparent">{t('page1.dependents.relationships.grandparent')}</option>
                  <option value="In-Law">{t('page1.dependents.relationships.inLaw')}</option>
                </select>
                {errors[`dependent${i}Relationship`] && (
                  <p className="text-xs text-red-600 mt-1">{errors[`dependent${i}Relationship`]}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('page1.dependents.ssn')} <span className="text-red-500">{t('common.required')}</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.dependents[i]?.ssn || ''}
                  onChange={(e) => handleDependentSSNChange(i, 'ssn', e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  maxLength="11"
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors[`dependent${i}Ssn`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                />
                {errors[`dependent${i}Ssn`] && (
                  <p className="text-xs text-red-600 mt-1">{errors[`dependent${i}Ssn`]}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('page1.dependents.dateOfBirth')} <span className="text-red-500">{t('common.required')}</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={displayDateValue(formData.dependents[i]?.dateOfBirth)}
                  onChange={(e) => handleDependentDateChange(i, 'dateOfBirth', e.target.value)}
                  placeholder="MM/DD/YYYY"
                  maxLength="10"
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors[`dependent${i}DateOfBirth`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                />
                {errors[`dependent${i}DateOfBirth`] ? (
                  <p className="text-xs text-red-600 mt-1">{errors[`dependent${i}DateOfBirth`]}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">MM/DD/YYYY</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('page1.dependents.activity', { number: i + 1 })} <span className="text-red-500">{t('common.required')}</span>
                </label>
                <select
                  value={formData.dependents[i]?.activity || ''}
                  onChange={(e) => handleDependentChange(i, 'activity', e.target.value)}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors[`dependent${i}Activity`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="Go to School">{t('page1.dependents.activities.school')}</option>
                  <option value="Go to Work">{t('page1.dependents.activities.work')}</option>
                  <option value="Neither">{t('page1.dependents.activities.neither')}</option>
                </select>
                {errors[`dependent${i}Activity`] && (
                  <p className="text-xs text-red-600 mt-1">{errors[`dependent${i}Activity`]}</p>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Certification Text */}
      <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
        <p className="text-xs text-gray-700 leading-relaxed">
          {t('page1.certification', { organizationName: organization?.name || '' })}
        </p>
      </div>

      {/* Next Button */}
      <button
        type="submit"
        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
      >
        {t('common.next')}
      </button>
    </form>
  );
}

export default Page1;